/**
 * Shared letter tab labels — same rules as Step 5 (LetterPreviewPage / Letters Ready for Review).
 */

const CANONICAL_BUREAUS = {
  equifax: 'Equifax',
  experian: 'Experian',
  transunion: 'TransUnion',
  'trans union': 'TransUnion',
};

/** Strategy / package routing keys — not user-facing dispute methods. */
const NON_METHOD_TOKENS = new Set([
  'bureau_package',
  'single_item',
  'grouped',
  'dispute_type',
  'inquiry_only',
  'personal_info_only',
  'accounts_special',
  'collections_exclusive',
  'standard',
  'factual',
  'factual_inaccuracy',
  'meth_factual_inaccuracy',
]);

function normToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

export function normalizeBureau(bureau) {
  if (!bureau) return bureau;
  const bureauLower = bureau.toLowerCase().trim();
  return CANONICAL_BUREAUS[bureauLower] || bureau.charAt(0).toUpperCase() + bureau.slice(1).toLowerCase();
}

function itemTypesFromFindings(findings) {
  const types = new Set();
  (findings || []).forEach((f) => {
    const cat = String(f?.category || f?.item_type || f?.context?.item_type || '')
      .toLowerCase()
      .trim();
    if (cat.includes('inquiry')) types.add('inquiry');
    else if (cat.includes('personal')) types.add('personal_info');
    else if (cat) types.add('account');
  });
  return Array.from(types);
}

function isNonMethodToken(value) {
  const token = normToken(value);
  if (!token) return true;
  if (NON_METHOD_TOKENS.has(token)) return true;
  if (token.endsWith('_package') && !token.includes('mixed')) return true;
  return false;
}

/**
 * Resolve classification/method from package + round metadata.
 * Never treat bureau_package (letter type) as the user-facing method.
 */
export function resolveRawMethod(pkg, round) {
  const ctx = pkg?.context || {};
  const meta = pkg?.metadata || ctx.metadata || {};
  const candidates = [
    pkg?.letter_method,
    pkg?.method_used,
    ctx.method_used,
    ctx.method_key,
    ctx.method,
    ctx.classification,
    ctx.resolved_method,
    ctx.display_method,
    ctx.package_subtype,
    pkg?.package_subtype,
    meta.method_key,
    meta.method_used,
    meta.classification,
    meta.prompt_family,
    pkg?.prompt_family,
    ctx.prompt_family,
    pkg?.template_family,
    round?.method_key,
    round?.resolved_method,
  ];

  for (const raw of candidates) {
    if (!raw || isNonMethodToken(raw)) continue;
    return String(raw).trim();
  }
  return '';
}

/** Build a letter-shaped object from a persisted package snapshot (Dispute Center). */
export function packageToLetterTabInput(pkg, round) {
  const ctx = pkg?.context || {};
  const methodUsed = resolveRawMethod(pkg, round);
  const itemTypes = Array.isArray(ctx.item_types) && ctx.item_types.length
    ? ctx.item_types
    : Array.isArray(pkg?.item_types) && pkg.item_types.length
      ? pkg.item_types
      : itemTypesFromFindings(pkg?.findings_snapshot);

  const mixedFlag =
    ctx.is_mixed ||
    round?.is_mixed ||
    String(methodUsed).toLowerCase().includes('mixed') ||
    String(methodUsed).toLowerCase().includes('multi') ||
    String(ctx.prompt_family || pkg?.prompt_family || '').toLowerCase().includes('mixed');

  return {
    bureau: pkg?.target_bureau || pkg?.bureau || round?.target_bureau,
    method_used: methodUsed,
    package_method: methodUsed,
    package_subtype: ctx.package_subtype || pkg?.package_subtype || '',
    prompt_family: ctx.prompt_family || pkg?.prompt_family || pkg?.template_family || '',
    item_types: itemTypes,
    is_mixed_package: Boolean(mixedFlag),
    package_name: ctx.package_label || ctx.method_name || pkg?.package_label || '',
    classification: ctx.classification || pkg?.classification || '',
  };
}

/** Method label only (no bureau) — Admin Control Center + Step 5 alignment. */
export function getMethodTypeLabel(letter) {
  const packageSubtype = (letter?.package_subtype || '').toLowerCase();
  const method = (letter?.method_used || letter?.package_method || letter?.classification || '').toLowerCase();
  const family = (letter?.prompt_family || '').toLowerCase();
  const itemTypes = letter?.item_types || [];

  if (letter?.is_mixed_package || method.includes('mixed') || method.includes('multi') || family.includes('mixed')) {
    return 'Mixed Package';
  }
  if (method.includes('metro2') || method.includes('metro_2') || method === 'metro 2' || family === 'metro2') {
    return 'Metro 2 Compliance';
  }

  const isDataBreach =
    method.includes('data_breach') ||
    method.includes('data breach') ||
    family === 'data_breach' ||
    method === 'data_breach_method';
  if (isDataBreach) {
    if (packageSubtype === 'inquiry' && !itemTypes.includes('personal_info')) {
      return 'Inquiry';
    }
    return 'Data Breach';
  }
  if (packageSubtype === 'inquiry' || method.includes('unauthorized_inquiry')) {
    return 'Inquiry';
  }
  if (packageSubtype === 'personal_info' || method.includes('personal_info') || method.includes('identity')) {
    return 'Personal Info';
  }
  if (
    method.includes('structural_recovery') ||
    method.includes('structural + recovery') ||
    family === 'structural_recovery_combo' ||
    (method.includes('structural') && method.includes('recovery'))
  ) {
    return 'Structural + Recovery';
  }
  if (method.includes('structural') || family === 'structural') {
    return 'Structural';
  }
  if (method.includes('recovery') || method.includes('debt_validation') || family === 'recovery') {
    return 'Recovery';
  }
  if (method.includes('isolated') || family === 'isolated') {
    return 'Isolated';
  }
  if (packageSubtype === 'account' || (method.includes('factual') && !method.includes('inaccuracy'))) {
    return 'Account';
  }
  if (letter?.package_name) {
    const named = String(letter.package_name)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    if (named && !named.toLowerCase().includes('bureau package')) {
      return named;
    }
  }
  return 'Dispute Letter';
}

/**
 * Human-readable tab label: "{Bureau} — {Package Type}"
 * Mirrors LetterPreviewPage Step 5 letter tabs.
 */
export function getLetterTabLabel(letter) {
  const bureau = normalizeBureau(letter?.bureau || 'Unknown');
  return `${bureau} — ${getMethodTypeLabel(letter)}`;
}

/** Resolved method for Letter Details Method card. */
export function getResolvedMethodLabel(pkg, round) {
  return getMethodTypeLabel(packageToLetterTabInput(pkg, round));
}

/** Letters in the same dispute round + method/package group (Select full package). */
export function getPackageGroupKey(pkg, round) {
  const roundId = pkg?.round_id || round?.id || 'unknown';
  const input = packageToLetterTabInput(pkg, round);
  const methodSig = getMethodTypeLabel(input).toLowerCase().replace(/\s+/g, '_');
  return `${roundId}::${methodSig}`;
}

/** Dispute Center Generated Letters table description column. */
export function getGeneratedLetterDescription(pkg, round) {
  const bureau = normalizeBureau(pkg?.target_bureau || pkg?.bureau || round?.target_bureau || 'Unknown');
  const tabLabel = getLetterTabLabel(packageToLetterTabInput(pkg, round));
  if (tabLabel && !tabLabel.endsWith('— Dispute Letter')) {
    return tabLabel;
  }
  return `${bureau} — Dispute Letter`;
}
